/* Author: Amisha Anshu */
/* Chat component */

import {
  Component,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  HostListener,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import { WorkflowContextService } from 'src/app/services';
import { DataService } from 'src/app/services/data.service';
import { svgs } from 'src/assets/export.assets';
// Added by Amisha on 2026-07-02.
// Reason: build the staff mail draft in a separate helper so the chat component stays focused on behavior and state.
import { buildChatMailDraft } from './chat-mail-content';

interface ChatWindowMessage {
  sender: 'user' | 'agent';
  text: string;
  date?: Date;
  status?: 'sent' | 'received';
  showDate?: boolean;
  internalMessageId?: string;
  senderName: string;
  profileImageUrl?: string;
}

@Component({
  selector: 'wm-chat-window',
  standalone: false,
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class ChatWindowComponent implements AfterViewInit {
  private readonly mobileBreakpoint = 959;
  @Input() applicationId = '';
  @Input() applicationType = '';
  // Added by Amisha on 2026-07-02.
  // Reason: the chat mail template needs the selected department name from the workflow applications table.
  @Input() departmentName = '';
  @Input() siteAddress = '';
  @Input() internalMessageId = '';
  // Added by Amisha on 2026-09-22.
  // Reason: only staff chat launched from an inline expansion should cover the
  // complete mobile viewport; other chat entry points retain the header offset.
  @Input() fullViewportMobile = false;
  // Added by Amisha on 2026-09-09.
  // Reason: restart the visual highlight whenever a parent reports an attempt to open a different chat.
  private lastAttentionTrigger = 0;
  private attentionHighlightTimer?: ReturnType<typeof setTimeout>;
  attentionHighlighted = false;

  @Input()
  set attentionTrigger(value: number) {
    if (!value || value === this.lastAttentionTrigger) {
      return;
    }

    this.lastAttentionTrigger = value;
    this.attentionHighlighted = false;

    if (this.attentionHighlightTimer) {
      clearTimeout(this.attentionHighlightTimer);
    }

    this.attentionHighlightTimer = setTimeout(() => {
      this.attentionHighlighted = true;
      this.cdr.detectChanges();
    });
  }
  @Output() closed = new EventEmitter<void>();
  @Output() errorOccurred = new EventEmitter<any>();
  @Output() messageSent = new EventEmitter<{ applicationId: string }>();

  @ViewChild('chatWindow', { static: true }) chatWindow!: ElementRef;
  @ViewChild('chatBody') chatBody!: ElementRef<HTMLDivElement>;
  @ViewChild('printSection') printSection!: ElementRef<HTMLDivElement>;
  @Output() unreadChanged = new EventEmitter<{ applicationId: string; unread: boolean }>();
  @Input() isStaff = false;
  hasUnreadMessages = false;



  constructor(
    private dataService: DataService,
    private context: WorkflowContextService,
    private cdr: ChangeDetectorRef
  ) { }

  newMessage = '';
  displayApplicationNumber = '';
  displayApplicantName = '';
  // Added by Amisha on 2026-07-02.
  // Reason: if applicant email exists we prefill Outlook; otherwise we still open mail with a blank To field.
  applicantEmailAddress = '';
  isDragging = false;
  offset = { x: 0, y: 0 };
  isLoadingMessages = false;
  noMessages = false;
  errorMessage = '';

  currentUserId = '';
  isMarkedReadByUser = false;

  showErrorModal = false;
  @Output() readStatusChanged = new EventEmitter<{
    applicationId: string;
    isRead: boolean;
  }>();

  errorModalConfig = {
    open: false,
    modalDescp: 'Error sending message',
    modalSubDescp: 'Unable to send your message. Please try again.',
    okBtn: true,
    yesNoBtn: false,
    closeBtn: false,
    status: 'error'
  };

  messages: ChatWindowMessage[] = [];
  markAsReadChecked = false;
  showMarkAsReadControl = false;
  isUpdating = false;
  printIcon = svgs.printIcon.name;
  printTimestamp = new Date();

  get isPrintDisabled(): boolean {
    return !this.isStaff || this.isLoadingMessages || !!this.errorMessage || this.messages.length === 0;
  }

  // Added by Amisha on 2026-07-02.
  // Reason: staff should trigger mail only when there is still an unread outgoing staff message for the applicant.
  get hasUnreadMessagesForApplicant(): boolean {
    return this.messages.some(
      m => m.sender === 'user' && m.status === 'sent'
    );
  }

  get isSendMailDisabled(): boolean {
    return this.isPrintDisabled || !this.hasUnreadMessagesForApplicant;
  }

  // Added by Amisha on 2026-07-02.
  // Reason: disabled Send Mail needs a clear explanation for staff instead of silently blocking the action.
  get sendMailDisabledReason(): string {
    if (!this.isStaff) {
      return 'Send Mail is available for staff only.';
    }

    if (this.isLoadingMessages) {
      return 'Messages are still loading.';
    }

    if (this.errorMessage) {
      return this.errorMessage;
    }

    if (this.messages.length === 0) {
      // Updated by Amisha on 2026-09-03.
      // Reason: use the approved empty-chat message consistently in the staff chat actions.
      return 'No chats available.';
    }

    if (!this.hasUnreadMessagesForApplicant) {
      return 'Send Mail is available only when there is an unread staff message for the applicant.';
    }

    return '';
  }

  get sendMailTooltip(): string {
    if (this.isSendMailDisabled) {
      return this.sendMailDisabledReason;
    }

    if (!this.applicantEmailAddress) {
      return 'Applicant email is not available. Outlook will open with a blank To field so you can enter it manually.';
    }

    return 'Open Outlook with the applicant email and chat notice.';
  }

  get isMobileOrTabletView(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= this.mobileBreakpoint;
  }

  get showHeaderClose(): boolean {
    return this.isMobileOrTabletView;
  }

  get showDragbarClose(): boolean {
    return !this.isMobileOrTabletView;
  }

  ngOnInit() {
    this.currentUserId = this.context.user?.id || '';
    this.loadMessages();
  }
  onMarkReadChange(checked: boolean): void {
    if (!checked) {
      return;
    }

    const unreadIds = this.messages
      .filter(
        m =>
          m.sender === 'agent' &&
          m.status === 'sent' &&
          !!m.internalMessageId
      )
      .map(m => m.internalMessageId as string);

    if (!unreadIds.length) {
      return;
    }

    this.isUpdating = true;

    forkJoin(unreadIds.map(id => this.dataService.markAsRead(id)))
      .pipe(finalize(() => (this.isUpdating = false)))
      .subscribe({
        next: () => {
          // update messages
          this.messages.forEach(m => {
            if (m.sender === 'agent' && m.status === 'sent') {
              m.status = 'received';
            }
          });

          this.hasUnreadMessages = false;
          this.isMarkedReadByUser = true;
          this.markAsReadChecked = true; // <-- stays checked

          this.readStatusChanged.emit({
            applicationId: this.applicationId,
            isRead: true
          });
        },
        error: () => {
          this.markAsReadChecked = false;
          this.isMarkedReadByUser = false;
        }
      });
  }






  loadMessages(): void {
    const applicationId = this.applicationId;
    this.isLoadingMessages = true;
    this.noMessages = false;
    this.errorMessage = '';

    this.dataService.getWorkflowApplicationMessages(applicationId).subscribe({
      next: (res) => {
        this.isLoadingMessages = false;

        if (res?.applicationNumber?.trim()) {
          this.displayApplicationNumber = res.applicationNumber.trim();
        }

        if (res?.applicantName?.trim()) {
          this.displayApplicantName = res.applicantName.trim();
        }

        // Added by Amisha on 2026-09-03.
        // Reason: the messages API now returns the authoritative applicant email.
        // Do not infer it from message senders because those users may be staff.
        this.applicantEmailAddress = this.getTrimmedEmail(res?.applicantEmail);

        if (!res?.messages || res.messages.length === 0) {
          this.noMessages = true;
          this.messages = [];
          this.hasUnreadMessages = false;
          return;
        }

        this.messages = res.messages.map((m: any) => this.mapMessage(m));

        this.hasUnreadMessages = this.messages.some(
          m => m.sender === 'agent' && m.status === 'sent'
        );
        this.showMarkAsReadControl = this.isStaff && this.hasUnreadMessages;

        if (!this.hasUnreadMessages) {
          this.markAsReadChecked = false;
          this.isMarkedReadByUser = false;
        }


        if (!this.isStaff && this.hasUnreadMessages) {
          this.messages.forEach(msg => {
            if (
              msg.sender === 'agent' &&
              msg.status === 'sent' &&
              msg.internalMessageId
            ) {
              this.dataService.markAsRead(msg.internalMessageId).subscribe(() => {
                msg.status = 'received';
              });
            }
          });

          this.hasUnreadMessages = false;
        }

        // Date separators
        for (let i = 0; i < this.messages.length; i++) {
          const cur = this.messages[i];
          const prev = this.messages[i - 1];
          cur.showDate = !prev || cur.date?.toDateString() !== prev.date?.toDateString();
        }

        setTimeout(() => this.scrollToBottom(), 0);
      },
      error: () => {
        this.isLoadingMessages = false;
        this.errorMessage = 'Unable to load messages. Please try again.';
      }
    });
  }




  private scrollToBottom(): void {
    try {
      const el = this.chatBody.nativeElement;
      el.scrollTop = el.scrollHeight;
    } catch { }
  }

  sendMessage() {
    if (!this.newMessage.trim()) {
      return;
    }

    const msg = this.newMessage;
    this.newMessage = '';

    const applicationId = this.applicationId;

    this.dataService.sendMessage(applicationId, msg).subscribe({
      next: () => {
        const senderName = this.context.user?.name || 'You';
        const newMsg: ChatWindowMessage = {
          sender: 'user' as 'user',
          text: msg,
          date: new Date(),
          status: 'sent' as 'sent',
          showDate: false,
          senderName,
          profileImageUrl: this.resolveProfileImageUrl(
            this.context.user?.userProfileImageURL || ''
          )
        };

        const prev = this.messages[this.messages.length - 1];

        newMsg.showDate =
          !prev || newMsg.date.toDateString() !== prev.date?.toDateString();

        this.messages.push(newMsg);

        this.noMessages = false;
        this.messageSent.emit({ applicationId: this.applicationId });
        if (this.isStaff) {
          const unreadIds = this.messages
            .filter(
              m =>
                m.sender === 'agent' &&
                m.status === 'sent' &&
                !!m.internalMessageId
            )
            .map(m => m.internalMessageId as string);

          if (unreadIds.length) {
            forkJoin(unreadIds.map(id => this.dataService.markAsRead(id)))
              .subscribe(() => {
                // update local UI
                this.messages.forEach(m => {
                  if (m.sender === 'agent' && m.status === 'sent') {
                    m.status = 'received';
                  }
                });

                this.hasUnreadMessages = false;
                this.isMarkedReadByUser = true;
                this.markAsReadChecked = true;

                // notify parent (badge update)
                this.readStatusChanged.emit({
                  applicationId: this.applicationId,
                  isRead: true
                });
              });
          }
        }

        setTimeout(() => this.scrollToBottom(), 0);
      },

      error: (err) => {
        this.errorOccurred.emit(err);
      },
    });
  }

  onPrint(): void {
    if (this.isPrintDisabled) {
      return;
    }

    this.printTimestamp = new Date();
    this.cdr.detectChanges();

    const printContents = this.printSection?.nativeElement?.innerHTML;

    if (!printContents) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1024,height=768');

    if (!printWindow) {
      const originalContents = document.body.innerHTML;

      document.body.innerHTML = `<style>${this.getPrintStyles()}</style>${printContents}`;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${this.getPrintTitle()}</title>
          <style>${this.getPrintStyles()}</style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);
    printWindow.document.close();

    printWindow.onafterprint = () => {
      printWindow.close();
    };

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 250);
  }

  onSendMail(): void {
    if (this.isSendMailDisabled) {
      return;
    }

    // Added by Amisha on 2026-07-02.
    // Reason: staff Send Mail should open the local mail client with the applicant notice already prepared.
    const mailDraft = buildChatMailDraft({
      applicantEmail: this.applicantEmailAddress,
      applicantName: this.displayApplicantName,
      // Added by Amisha on 2026-09-07.
      // Reason: include the API-provided application number in the staff chat email details.
      applicationNumber: this.displayApplicationNumber,
      departmentName: this.departmentName,
      applicationType: this.applicationType,
      siteAddress: this.siteAddress
    });

    const mailtoUrl =
      `mailto:${mailDraft.to ? encodeURIComponent(mailDraft.to) : ''}` +
      `?subject=${encodeURIComponent(mailDraft.subject)}` +
      `&body=${encodeURIComponent(mailDraft.body)}`;

    window.location.href = mailtoUrl;
  }

  closeChat() {
    const el = this.chatWindow?.nativeElement;

    if (el && el.parentNode === document.body) {
      document.body.removeChild(el); // 🔥 remove manually
    }

    this.closed.emit();
  }
  ngOnDestroy() {
    // Added by Amisha on 2026-09-09.
    // Reason: prevent the deferred attention highlight from running after the chat component is destroyed.
    if (this.attentionHighlightTimer) {
      clearTimeout(this.attentionHighlightTimer);
    }

    const el = this.chatWindow?.nativeElement;

    if (el && el.parentNode === document.body) {
      document.body.removeChild(el);
    }
  }

  // --- Dragging for desktop ---
  onDragStart(event: MouseEvent) {
    // Added by Amisha on 2026-09-09.
    // Reason: allow dragging from any non-interactive chat area while preserving normal control behavior.
    if (window.innerWidth <= this.mobileBreakpoint || event.button !== 0) { return; };

    const target = event.target as HTMLElement | null;

    if (target?.closest('button, input, textarea, a, mat-checkbox, [role="button"]')) {
      return;
    }

    event.preventDefault();

    this.isDragging = true;
    const rect = this.chatWindow.nativeElement.getBoundingClientRect();
    this.offset = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };

    this.chatWindow.nativeElement.style.transform = 'none';
  }

  onAttentionAnimationEnd(event: AnimationEvent): void {
    // Added by Amisha on 2026-09-09.
    // Reason: clear only the chat attention state and ignore bubbled loading animations from child elements.
    if (event.animationName === 'chat-attention-highlight') {
      this.attentionHighlighted = false;
    }
  }

  @HostListener('document:mouseup')
  onDragEnd() {
    this.isDragging = false;
  }

  @HostListener('document:mousemove', ['$event'])
  onDragMove(event: MouseEvent) {
    if (window.innerWidth <= this.mobileBreakpoint) { return; };

    if (this.isDragging && this.chatWindow) {
      const win = this.chatWindow.nativeElement;
      win.style.left = `${event.clientX - this.offset.x}px`;
      win.style.top = `${event.clientY - this.offset.y}px`;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.applyResponsivePosition();
  }

  ngAfterViewInit() {
    const el = this.chatWindow.nativeElement as HTMLElement;

    document.body.appendChild(el); // 🔥 FIX

    el.style.position = 'fixed';
    el.style.zIndex = '2147483647';

    this.applyResponsivePosition();

    setTimeout(() => this.scrollToBottom(), 0);
  }

  onAvatarError(message: ChatWindowMessage): void {
    message.profileImageUrl = '';
  }

  onAvatarLoad(message: ChatWindowMessage, event: Event): void {
    const image = event.target as HTMLImageElement | null;

    if (!image || !message.profileImageUrl) {
      return;
    }

    if (this.shouldFallbackToIcon(image)) {
      message.profileImageUrl = '';
    }
  }

  private mapMessage(message: any): ChatWindowMessage {
    const isCurrentUser = message.sentById === this.currentUserId;
    const senderName = this.getSenderName(message, isCurrentUser);

    return {
      sender: isCurrentUser ? 'user' : 'agent',
      text: message.message,
      date: message.sentOn ? new Date(message.sentOn) : undefined,
      status: message.hasBeenRead ? 'received' : 'sent',
      internalMessageId: message.id,
      senderName,
      profileImageUrl: this.getProfileImageUrl(message, isCurrentUser)
    };
  }

  private getSenderName(message: any, isCurrentUser: boolean): string {
    if (isCurrentUser) {
      return this.context.user?.name || message.sentBy?.name || 'You';
    }

    return message.sentBy?.name || this.displayApplicantName || 'Unknown';
  }

  private getProfileImageUrl(message: any, isCurrentUser: boolean): string {
    if (isCurrentUser) {
      return this.resolveProfileImageUrl(
        this.context.user?.userProfileImageURL || message.sentBy?.userProfileImageURL || ''
      );
    }

    return this.resolveProfileImageUrl(message.sentBy?.userProfileImageURL || '');
  }

  private getTrimmedEmail(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private resolveProfileImageUrl(path: string): string {
    if (!path) {
      return '';
    }

    const normalizedPath = path.trim();

    return normalizedPath.startsWith('http')
      ? normalizedPath
      : `${window.location.origin}${normalizedPath}`;
  }

  private shouldFallbackToIcon(image: HTMLImageElement): boolean {
    try {
      const sampleSize = 24;
      const canvas = document.createElement('canvas');
      canvas.width = sampleSize;
      canvas.height = sampleSize;

      const context = canvas.getContext('2d');

      if (!context) {
        return false;
      }

      context.drawImage(image, 0, 0, sampleSize, sampleSize);

      const { data } = context.getImageData(0, 0, sampleSize, sampleSize);
      const totalPixels = sampleSize * sampleSize;

      let veryLightPixels = 0;
      let darkPixels = 0;
      let saturatedPixels = 0;

      for (let index = 0; index < data.length; index += 4) {
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        const alpha = data[index + 3];

        if (alpha < 10) {
          veryLightPixels += 1;
          continue;
        }

        const maxChannel = Math.max(red, green, blue);
        const minChannel = Math.min(red, green, blue);

        if (red > 235 && green > 235 && blue > 235) {
          veryLightPixels += 1;
        }

        if (maxChannel < 90) {
          darkPixels += 1;
        }

        if (maxChannel - minChannel > 45) {
          saturatedPixels += 1;
        }
      }

      const lightRatio = veryLightPixels / totalPixels;
      const darkRatio = darkPixels / totalPixels;
      const saturatedRatio = saturatedPixels / totalPixels;

      return lightRatio > 0.45 && darkRatio > 0.03 && darkRatio < 0.28 && saturatedRatio < 0.12;
    } catch {
      return false;
    }
  }

  private getPrintTitle(): string {
    return this.displayApplicationNumber
      ? `Chat - ${this.displayApplicationNumber}`
      : 'Chat';
  }

  private getPrintStyles(): string {
    return `
      @page {
        margin: 12mm;
      }

      body {
        margin: 0;
        color: #000;
        font-family: "Times New Roman", Times, serif;
        font-size: 12px;
      }

      .chat-print-sheet {
        width: 100%;
      }

      .chat-print-generated-on {
        margin-bottom: 12px;
        font-size: 11px;
      }

      .chat-print-meta {
        display: table;
        width: 100%;
        margin-bottom: 18px;
      }

      .chat-print-meta-column {
        display: table-cell;
        width: 50%;
        vertical-align: top;
      }

      .chat-print-meta-item {
        margin-bottom: 4px;
      }

      .chat-print-meta-label {
        display: inline-block;
        min-width: 110px;
        font-weight: 700;
      }

      .chat-print-title {
        margin: 0 0 6px;
        font-size: 22px;
        font-weight: 700;
      }

      .chat-print-table {
        width: 100%;
        border-collapse: collapse;
        table-layout: fixed;
      }

      .chat-print-table th,
      .chat-print-table td {
        border: 1px solid #cfd8dc;
        padding: 4px 6px;
        text-align: left;
        vertical-align: top;
      }

      .chat-print-table th {
        font-weight: 700;
      }

      .chat-print-col-number {
        width: 56px;
      }

      .chat-print-col-date {
        width: 240px;
      }

      .chat-print-comment-row td {
        white-space: pre-wrap;
        word-break: break-word;
      }

      .chat-print-comment-label {
        font-weight: 700;
      }

      .chat-print-empty {
        border: 1px solid #cfd8dc;
        padding: 12px;
      }
    `;
  }

  private applyResponsivePosition(): void {
    const el = this.chatWindow?.nativeElement as HTMLElement | undefined;

    if (!el) {
      return;
    }

    if (window.innerWidth <= this.mobileBreakpoint) {
      el.style.left = '0';
      el.style.right = '0';
      el.style.top = this.fullViewportMobile ? '0' : '70px';
      el.style.bottom = '0';
      el.style.height = this.fullViewportMobile
        ? '100dvh'
        : 'calc(100dvh - 70px)';
      el.style.maxHeight = this.fullViewportMobile
        ? '100dvh'
        : 'calc(100dvh - 70px)';
      return;
    }

    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.left = `${Math.max((window.innerWidth - el.offsetWidth) / 2, 24)}px`;
    el.style.top = `${Math.max((window.innerHeight - el.offsetHeight) / 2, 24)}px`;
  }
}
